import { Ref, useEffect, useEffectEvent, useImperativeHandle, useRef } from 'react';
import { FileState, useFileUpload } from '@/hooks/useFileUpload';

export interface StanFileUploadProps {
    accept?: string;
    multiple?: boolean;
    options?: object;
    retry?: number;
    confirmUpload?: boolean;
    objectType?: string;
    onFilePicked?: (files: File[]) => void;
    onUploaded?: (payload: { name: string; src: string }) => void;
    onProgress?: (payload: { name: string; progress: number }) => void;
    onError?: (error: Error) => void;
    onFileStatesChange?: (fileStates: FileState[]) => void;
    ref?: Ref<StanFileUploadRef>;
}

export interface StanFileUploadRef {
    onPickFile: () => void;
    addfiles: (files: FileList | File[]) => void;
    deleteFile: (idx: number) => void;
    clearFile: () => void;
    upload: () => Promise<void>;
    getFileStates: () => FileState[];
}

export const StanFileUpload = ({
    accept = '*',
    multiple = false,
    objectType = 'unknown',
    confirmUpload = true,
    onFilePicked,
    onUploaded,
    onProgress,
    onError,
    onFileStatesChange,
    ref,
}: StanFileUploadProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const { fileStates, uploadAllFiles, clearFiles, deleteFile, addFiles } = useFileUpload({
        objectType,
        onProgress,
        onUploaded,
        onError,
    });

    // Notify parent of file states changes
    const handleFileStatesChange = useEffectEvent((states: FileState[]) => {
        onFileStatesChange?.(states);
    });

    const handleAutoUpload = useEffectEvent(() => {
        uploadAllFiles();
    });

    useEffect(() => {
        // Auto-upload if confirmUpload is true
        if (confirmUpload) {
            handleAutoUpload();
        }
        handleFileStatesChange(fileStates);
    }, [fileStates, confirmUpload]);

    useImperativeHandle(ref, () => ({
        onPickFile: () => {
            inputRef.current?.click();
        },
        deleteFile: (idx: number) => {
            deleteFile(idx);
        },
        addfiles: (files: FileList | File[]) => {
            addSelectedFiles(files);
        },
        clearFile: () => {
            if (inputRef.current) inputRef.current.value = '';
            clearFiles();
        },
        upload: uploadAllFiles,
        getFileStates: () => fileStates,
    }));

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) {
            onFilePicked?.([]);
            return;
        }

        addSelectedFiles(files);
    };

    const addSelectedFiles = async (files: FileList | File[]) => {
        const fileArray = files instanceof FileList ? Array.from(files) : files;
        onFilePicked?.(fileArray);
        addFiles(fileArray);
    };

    return (
        <input
            type='file'
            ref={inputRef}
            style={{ display: 'none' }}
            accept={accept}
            multiple={multiple}
            onChange={handleFileChange}
        />
    );
};

StanFileUpload.displayName = 'StanFileUpload';
